import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, CheckCircle2, LockKeyhole } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6" data-testid="reset-password-page">
      <a href="#main-content" className="skip-to-content">Skip to main content</a>
      <main id="main-content" tabIndex={-1} className="w-full max-w-md outline-none">
        <div className="flex justify-center mb-8">
          <img src="/lifelink-logo.png" alt="LIFE Link" className="h-14 object-contain" />
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          {done ? (
            <div className="text-center" data-testid="reset-password-success">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#3d7a22]/10 mb-4">
                <CheckCircle2 className="w-7 h-7 text-[#3d7a22]" aria-hidden="true" />
              </span>
              <h1 className="font-['Manrope'] text-2xl font-bold text-[#1A1A1A] mb-2">Password updated!</h1>
              <p className="text-[#5C5C5C] text-sm mb-6">Your password has been reset. You can now sign in with your new password.</p>
              <Link to="/login">
                <Button className="bg-[#34657f] hover:bg-[#2a5060] text-white font-semibold rounded-lg h-11 px-8" data-testid="go-to-login-btn">
                  Go to Sign In
                </Button>
              </Link>
            </div>
          ) : !token ? (
            <div className="text-center" data-testid="reset-password-no-token">
              <h1 className="font-['Manrope'] text-2xl font-bold text-[#1A1A1A] mb-2">Invalid link</h1>
              <p className="text-[#5C5C5C] text-sm mb-6">This reset link is missing its token. Please use the link from your email, or request a new one.</p>
              <Link to="/forgot-password" className="text-[#34657f] font-semibold underline text-sm">Request a new link</Link>
            </div>
          ) : (
            <>
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#34657f]/10 mb-4">
                <LockKeyhole className="w-6 h-6 text-[#34657f]" aria-hidden="true" />
              </span>
              <h1 className="font-['Manrope'] text-2xl font-bold text-[#1A1A1A] mb-1">Choose a new password</h1>
              <p className="text-[#5C5C5C] text-sm mb-6">Must be at least 6 characters.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 px-4 pr-12 border-slate-300 focus:border-[#34657f]"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded text-slate-500 hover:text-slate-700"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                  <Input
                    id="confirm-new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="h-12 px-4 border-slate-300 focus:border-[#34657f]"
                    data-testid="reset-confirm-input"
                  />
                </div>
                {error && (
                  <p className="text-red-700 text-sm" role="alert" data-testid="reset-password-error">{error}</p>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#34657f] hover:bg-[#2a5060] text-white font-semibold rounded-lg"
                  data-testid="reset-password-submit-btn"
                >
                  {loading ? 'Updating…' : 'Reset Password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
